import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, TextField } from '@mui/material'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { gsap } from 'gsap'
import { useTranslation } from 'react-i18next'
import { authenticatedFetch } from '../../../services/api/apiClient'
import { apiEndpoints } from '../../../consts'
import { DrawerCard, DrawerBadge } from '../../DrawerComponents'
import styles from '../CallDrawerModern.module.scss'

export const RatingCommentsCard = ({
  selectedRow,
  currentVote,
  onUpvote,
  onDownvote,
  userId
}) => {
  const { t } = useTranslation()

  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')

  const upvoteRef = useRef(null)
  const downvoteRef = useRef(null)
  const isInitialLoadRef = useRef(true)

  const getCallId = useCallback(() => {
    return selectedRow?.id || selectedRow?.call_id || selectedRow?.[3] || null
  }, [selectedRow])

  // Latest selected call id, readable from async handlers without a stale closure.
  const callIdRef = useRef(getCallId())
  useEffect(() => {
    callIdRef.current = getCallId()
  }, [getCallId])

  const fetchComments = useCallback(async () => {
    const callId = getCallId()
    if (!callId) return

    try {
      const response = await authenticatedFetch(apiEndpoints.commentsByCall(callId))
      if (response.ok) {
        const data = await response.json()
        setComments(data)
      }
    } catch (error) {
      console.error('Error fetching comments:', error)
    }
  }, [getCallId])

  /* eslint-disable react-hooks/set-state-in-effect -- fetch and reset on selectedRow change */
  useEffect(() => {
    if (selectedRow) {
      fetchComments()
      isInitialLoadRef.current = true
    } else {
      setComments([])
    }
  }, [selectedRow, fetchComments])
  /* eslint-enable react-hooks/set-state-in-effect */

  /* istanbul ignore next */
  const animateVoteButton = (button, voteType) => {
    gsap.timeline()
      .to(button, { scale: 0.85, duration: 0.1, ease: 'power2.in' })
      .to(button, { scale: 1.15, duration: 0.2, ease: 'back.out(1.7)' })
      .to(button, { scale: 1, duration: 0.15, ease: 'power1.out' })

    const parentElement = button.closest(`.${styles.ratingVoteSection}`)
    if (parentElement) {
      const numParticles = 6
      const buttonRect = button.getBoundingClientRect()
      const containerRect = parentElement.getBoundingClientRect()
      const buttonCenterX = buttonRect.left - containerRect.left + buttonRect.width / 2
      const buttonCenterY = buttonRect.top - containerRect.top + buttonRect.height / 2

      const computedStyle = getComputedStyle(document.documentElement)
      const particleColor = voteType === 'up'
        ? computedStyle.getPropertyValue('--color-success').trim() || '#4CAF50'
        : computedStyle.getPropertyValue('--color-error').trim() || '#F44336'

      for (let i = 0; i < numParticles; i++) {
        const particle = document.createElement('div')
        particle.style.cssText = `
          position: absolute;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background-color: ${particleColor};
          pointer-events: none;
          z-index: 10;
        `
        parentElement.appendChild(particle)

        const angle = (Math.PI * 2 * i) / numParticles
        const distance = 25 + Math.random() * 15

        gsap.set(particle, { x: buttonCenterX, y: buttonCenterY, opacity: 1 })
        gsap.to(particle, {
          x: buttonCenterX + Math.cos(angle) * distance,
          y: buttonCenterY + Math.sin(angle) * distance,
          opacity: 0,
          duration: 0.4,
          ease: 'power2.out',
          onComplete: () => particle.remove()
        })
      }
    }
  }

  const handleAddComment = async () => {
    const callId = getCallId()
    if (!newComment.trim() || !callId || !userId) return

    try {
      const response = await authenticatedFetch(apiEndpoints.comments, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          call_id: callId,
          user_id: userId,
          comment_text: newComment
        })
      })
      if (response.ok) {
        if (callIdRef.current !== callId) return
        setNewComment('')
        fetchComments()
      }
    } catch (error) {
      console.error('Error adding comment:', error)
    }
  }

  const renderBadge = () => {
    if (comments.length > 0) {
      return (
        <DrawerBadge variant="info">
          <FontAwesomeIcon icon="comment" />
          <span>{comments.length}</span>
        </DrawerBadge>
      )
    }
    return null
  }

  return (
    <DrawerCard
      title={t('calls.drawer.rateThisCall')}
      icon="star"
      badge={renderBadge()}
    >
      <Box className={styles.ratingCardContent}>
        {/* Vote Section */}
        <Box className={styles.ratingVoteSection}>
          <Typography className={styles.ratingVoteLabel}>
            How was this call?
          </Typography>
          <Box className={styles.ratingVoteButtons}>
            <button
              ref={upvoteRef}
              onClick={(e) => {
                isInitialLoadRef.current = false
                animateVoteButton(e.currentTarget, 'up')
                onUpvote()
              }}
              className={`${styles.ratingVoteBtn} ${styles.ratingVoteBtnUp} ${currentVote === 'up' ? styles.ratingVoteBtnActive : ''}`}
              type="button"
            >
              <ThumbUpIcon className={styles.ratingVoteIcon} />
              <span>Good</span>
            </button>
            <button
              ref={downvoteRef}
              onClick={(e) => {
                isInitialLoadRef.current = false
                animateVoteButton(e.currentTarget, 'down')
                onDownvote()
              }}
              className={`${styles.ratingVoteBtn} ${styles.ratingVoteBtnDown} ${currentVote === 'down' ? styles.ratingVoteBtnActive : ''}`}
              type="button"
            >
              <ThumbDownIcon className={styles.ratingVoteIcon} />
              <span>Poor</span>
            </button>
          </Box>
        </Box>

        {/* Divider */}
        <Box className={styles.ratingDivider} />

        {/* Comments Section */}
        <Box className={styles.ratingCommentsSection}>
          <Typography className={styles.ratingCommentsTitle}>
            <FontAwesomeIcon icon="comments" />
            Comments {comments.length > 0 && `(${comments.length})`}
          </Typography>

          {/* Comments List */}
          {comments.length > 0 ? (
            <Box className={styles.ratingCommentsList}>
              {comments.map((comment) => (
                <Box key={comment.id} className={styles.ratingCommentItem}>
                  <Typography className={styles.ratingCommentText}>
                      {comment.comment_text}
                    </Typography>
                    <Box className={styles.ratingCommentMeta}>
                      <span className={styles.ratingCommentAuthor}>
                        <FontAwesomeIcon icon="user" />
                        {comment.nickname || 'Anonymous'}
                      </span>
                      <span className={styles.ratingCommentDate}>
                        {new Date(comment.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </span>
                    </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box className={styles.ratingNoComments}>
              <FontAwesomeIcon icon="comment-dots" />
              <span>No comments yet</span>
            </Box>
          )}

          {/* Add Comment Input */}
          <Box className={styles.ratingAddComment}>
            <TextField
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              fullWidth
              variant="outlined"
              size="small"
              multiline
              minRows={2}
              className={styles.ratingCommentInput}
            />
            <button
              className={styles.ratingSubmitBtn}
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              type="button"
            >
              <FontAwesomeIcon icon="paper-plane" />
              Send
            </button>
          </Box>
        </Box>
      </Box>
    </DrawerCard>
  )
}

RatingCommentsCard.propTypes = {
  selectedRow: PropTypes.object,
  currentVote: PropTypes.oneOf(['up', 'down', null]),
  onUpvote: PropTypes.func,
  onDownvote: PropTypes.func,
  userId: PropTypes.string
}

export default RatingCommentsCard
